import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from "@microsoft/signalr";
import { BehaviorSubject, Observable, timer } from "rxjs";

export abstract class BaseHubService {
    protected hubConnection: HubConnection;
    private connectionStateSubject = new BehaviorSubject<HubConnectionState>(HubConnectionState.Disconnected);
    private reconnectAttempts = 0;
    private maxReconnectAttempts = 10;
    private isManuallyDisconnected = false;
    private connectionLostTimestamp: Date | null = null;
    private readonly maxDisconnectionTime = 30 * 60 * 1000; // 30 minutes in milliseconds

    public connectionState$ = this.connectionStateSubject.asObservable();

    public get connection(): HubConnection {
        return this.hubConnection;
    }

    constructor() { }

    /**
     * Get the URL for the hub connection
     */
    protected abstract getHubUrl(): string;

    /**
     * Register server-sent event handlers
     */
    protected abstract registerServerEvents(): void;

    /**
     * Refresh data after a successful reconnection
     * @param longDisconnection True if the disconnection lasted longer than the threshold
     */
    protected abstract refreshDataAfterReconnection(longDisconnection: boolean): void;

    /**
     * Get the access token for the hub connection (optional)
     */
    protected async getAccessToken(): Promise<string | null> {
        return null;
    }

    /**
     * Initializes the hub connection
     */
    protected initializeConnection(): void {
        const url = this.getHubUrl();
        const builder = new HubConnectionBuilder()
            .withUrl(url, {
                accessTokenFactory: () => this.getAccessToken()
            })
            .withAutomaticReconnect([0, 2000, 5000, 10000, 30000, 60000])
            .configureLogging(LogLevel.Information);

        this.hubConnection = builder.build();
        this.setupConnectionEventHandlers();
    }

    /**
     * Sets up connection event handlers
     */
    private setupConnectionEventHandlers(): void {
        this.hubConnection.onclose((error) => {
            console.log(`Connection to ${this.getHubUrl()} closed:`, error);
            this.connectionStateSubject.next(HubConnectionState.Disconnected);
            if (!this.isManuallyDisconnected) {
                if (!this.connectionLostTimestamp) {
                    this.connectionLostTimestamp = new Date();
                }
                this.handleConnectionLoss();
            }
        });

        this.hubConnection.onreconnecting((error) => {
            console.log(`Connection to ${this.getHubUrl()} reconnecting:`, error);
            this.connectionStateSubject.next(HubConnectionState.Reconnecting);
            if (!this.connectionLostTimestamp) {
                this.connectionLostTimestamp = new Date();
            }
        });

        this.hubConnection.onreconnected((connectionId) => {
            console.log(`Connection to ${this.getHubUrl()} reconnected:`, connectionId);
            this.connectionStateSubject.next(HubConnectionState.Connected);
            this.reconnectAttempts = 0;
            
            const longDisconnection = this.hasBeenDisconnectedTooLong();
            this.registerServerEvents();
            this.refreshDataAfterReconnection(longDisconnection);
            
            this.connectionLostTimestamp = null;
        });
    }

    /**
     * Starts the connection
     */
    public async connect(): Promise<void> {
        if (!this.hubConnection) {
            this.initializeConnection();
        }

        if (this.hubConnection.state === HubConnectionState.Connected || 
            this.hubConnection.state === HubConnectionState.Connecting) {
            return;
        }

        this.isManuallyDisconnected = false;
        await this.startConnection();
    }

    /**
     * Stops the connection
     */
    public async disconnect(): Promise<void> {
        this.isManuallyDisconnected = true;
        this.connectionLostTimestamp = null;
        if (this.hubConnection) {
            this.connectionStateSubject.next(HubConnectionState.Disconnected);
            await this.hubConnection.stop();
        }
    }

    /**
     * Internal method to start the hub connection with retry logic
     */
    private async startConnection(): Promise<void> {
        try {
            await this.hubConnection.start();
            console.log(`Connection to ${this.getHubUrl()} started`);
            this.connectionStateSubject.next(HubConnectionState.Connected);
            this.reconnectAttempts = 0;
            this.connectionLostTimestamp = null;
            this.registerServerEvents();
        } catch (error) {
            console.error(`Error while starting connection to ${this.getHubUrl()}:`, error);
            this.connectionStateSubject.next(HubConnectionState.Disconnected);
            await this.handleConnectionFailure(error);
        }
    }

    /**
     * Handles connection failures with exponential backoff
     */
    private async handleConnectionFailure(error: any): Promise<void> {
        if (this.isManuallyDisconnected) return;

        this.reconnectAttempts++;

        if (this.reconnectAttempts <= this.maxReconnectAttempts) {
            const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts - 1), 30000);
            console.log(`Retrying connection to ${this.getHubUrl()} in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

            timer(delay).subscribe(() => {
                if (!this.isManuallyDisconnected) {
                    this.startConnection();
                }
            });
        } else {
            console.error(`Max reconnection attempts reached for ${this.getHubUrl()}. Connection permanently failed.`);
        }
    }

    /**
     * Logic to handle connection loss
     */
    protected handleConnectionLoss(): void {
        // Can be overridden by subclasses if needed
    }

    /**
     * Checks if the connection has been down for too long
     */
    public hasBeenDisconnectedTooLong(): boolean {
        if (!this.connectionLostTimestamp) {
            return false;
        }

        const disconnectionDuration = Date.now() - this.connectionLostTimestamp.getTime();
        return disconnectionDuration > this.maxDisconnectionTime;
    }

    /**
     * Gets the current connection state
     */
    public getConnectionState(): HubConnectionState {
        return this.hubConnection ? this.hubConnection.state : HubConnectionState.Disconnected;
    }

    /**
     * Checks if the hub is currently connected
     */
    public isConnected(): boolean {
        return this.getConnectionState() === HubConnectionState.Connected;
    }

    /**
     * Gets the duration of the current disconnection in milliseconds
     */
    public getDisconnectionDuration(): number | null {
        if (!this.connectionLostTimestamp) {
            return null;
        }
        return Date.now() - this.connectionLostTimestamp.getTime();
    }
}

